/* eslint-disable curly */
import { useEffect, useState } from 'react';
import { Button, TextInput } from '@wot/components';
import { MdClose } from 'react-icons/md';
import { toast } from 'react-toastify';
import { SubjectDTO } from '../../../interfaces/curriculum';
import { ImageUpload } from '../../../components/ImageUpload';
import { API_URL } from '../../../utils/constants';

interface SubjectFormError {
  name?: boolean;
  skills?: boolean;
}

export function SubjectForm({
  onSave,
  initialData,
  isEditMode = false
}: {
  onSave: (subject: SubjectDTO) => void;
  initialData?: SubjectDTO | null;
  isEditMode?: boolean;
}) {
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;

  const [curriculumPdfFile, setCurriculumPdfFile] = useState<File | null>(null);
  const [isPdfUploading, setIsPdfUploading] = useState(false);
  const [pdfUploadError, setPdfUploadError] = useState<string | null>(null);
  const [form, setForm] = useState<SubjectDTO>({
    _id:'',
    id: '',
    name: '',
    tag: '',
    ageGroup: '',
    heroImageUrl: '',
    description: '',
    skills: [],
    meta: { title: '', description: '', focusKeyword: '' },
    eligibility: { ageGroup: '', criteria: '' },
    highlights: [],
    curriculum: [],
    durationOptions: [],
    flexibility: [],
    faqs: [],
    heroData: {
      title: '',
      description: '',
      // primaryButtonText: "",
      // secondaryButtonText: "",
      stats: [],
      videoCard: { title: '', description: '' }
    },
    curriculumPdfUrl: ''
  });

  const [errors, setErrors] = useState<SubjectFormError>();

  useEffect(() => {
    if (initialData) {
       setForm(initialData);
    }
  }, [initialData]);

  const validateForm = () => {
    const newErrors: SubjectFormError = {
      name: !form.name,
      skills: form.skills.length === 0
    };
    setErrors(newErrors);

    if (Object.values(newErrors).some(Boolean)) {
      toast.error('Please fill all required fields');
      return false;
    }
    return true;
  };

  const handleSave = () => {
    if (!validateForm()) return;
    // console.log("Subject Form Data:", form);
    // console.log("Curriculum PDF File:", curriculumPdfFile);
    onSave(form);
    // toast.success("Subject added! Data logged to console.");
  };

  const nextStep = () => setCurrentStep((prev) => (prev < totalSteps ? prev + 1 : prev));
  const prevStep = () => setCurrentStep((prev) => (prev > 1 ? prev - 1 : prev));

  const handleArrayChange = (arrayName: keyof SubjectDTO, index: number, field: string, value: any) => {
    const newArray = [...(form[arrayName] as any[])];
    newArray[index] = { ...newArray[index], [field]: value };
    setForm({ ...form, [arrayName]: newArray });
  };

  const addToArray = (arrayName: keyof SubjectDTO, newItem: any) => {
    setForm({ ...form, [arrayName]: [...(form[arrayName] as any[]), newItem] });
  };

  const removeFromArray = (arrayName: keyof SubjectDTO, index: number) => {
    setForm({ ...form, [arrayName]: (form[arrayName] as any[]).filter((_, i) => i !== index) });
  };

  const handlePdfFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setIsPdfUploading(true);
      setPdfUploadError(null);
      setCurriculumPdfFile(file);

      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await fetch(`${API_URL}/upload`, {
          method: 'POST',
          body: formData
        });

        if (response.ok) {
          const result = await response.json();
          setForm((prevForm) => ({ ...prevForm, curriculumPdfUrl: result.data }));
          toast.success('PDF uploaded successfully!');
        } else {
          setPdfUploadError('PDF upload failed.');
          toast.error('PDF upload failed.');
        }
      } catch (err) {
        setPdfUploadError('An error occurred during PDF upload.');
        toast.error('An error occurred during PDF upload.');
      } finally {
        setIsPdfUploading(false);
      }
    }
  };

  return (
    <div className="flex flex-col gap-6 max-w-4xl mx-auto p-4">
      <div className="text-center font-bold">
        Step {currentStep} of {totalSteps}
      </div>

      {currentStep === 1 && (
        <div className="p-4 border rounded animate-fadeIn">
          <h2 className="text-xl font-bold mb-4">Step 1: Basic Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextInput value={form.id} placeholder="e.g., vedic-maths"  onChange={undefined} />
            <TextInput
              value={form.name}
              onChange={(val: string) => {
                const newId = val
                  .toLowerCase()
                  .replace(/\s+/g, '-')
                  .replace(/[^a-z0-9-]/g, '');
                setForm({ ...form, name: val, id: newId });
              }}
              placeholder="e.g., Vedic Maths Course"
            />
            {errors?.name && <p className="text-xs text-red-500 col-span-2">Please enter subject name</p>}
            <TextInput
              value={form.tag}
              onChange={(val: any) => setForm({ ...form, tag: val })}
              placeholder="e.g., Maths Course"
            />
            <TextInput
              value={form.ageGroup}
              onChange={(val: any) => setForm({ ...form, ageGroup: val })}
              placeholder="e.g., 10 years onwards"
            />
            <div className="md:col-span-2">
              <ImageUpload
                label="change"
                previewUrl={form.heroImageUrl}
                onUploadComplete={(url) => {
                  setForm({ ...form, heroImageUrl: url });
                }}
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Subject description"
                className="w-full p-2 border rounded"
                rows={4}
              />
            </div>
          </div>
        </div>
      )}

      {currentStep === 2 && (
        <div className="p-4 border rounded animate-fadeIn">
          <h2 className="text-xl font-bold mb-4">Step 2: Details</h2>
          <div className="p-4 border rounded mb-4">
            <h3 className="text-sm font-medium">Skills</h3>
            {form.skills.map((skill, i) => (
              <div key={i} className="flex items-center gap-2 mt-2">
                <TextInput
                  value={skill}
                  onChange={(val: string) => {
                    const newSkills = [...form.skills];
                    newSkills[i] = val;
                    setForm({ ...form, skills: newSkills });
                  }}
                  placeholder="Skill"
                />
                <MdClose className="cursor-pointer text-red-500" onClick={() => removeFromArray('skills', i)} />
              </div>
            ))}
            <Button text="Add Skill" onClick={() => addToArray('skills', '')} className="mt-2" />
            {errors?.skills && <p className="text-xs text-red-500">Please add at least one skill</p>}
          </div>

          <div className="p-4 border rounded mb-4">
            <h3 className="text-sm font-medium">Highlights</h3>
            {form.highlights.map((h, i) => (
              <div key={i} className="flex items-center gap-2 mt-2">
                <TextInput
                  value={h.text}
                  onChange={(val: any) => handleArrayChange('highlights', i, 'text', val)}
                  placeholder="Highlight text"
                />
                <MdClose className="cursor-pointer text-red-500" onClick={() => removeFromArray('highlights', i)} />
              </div>
            ))}
            <Button
              text="Add Highlight"
              onClick={() => addToArray('highlights', { icon: '', text: '' })}
              className="mt-2"
            />
          </div>

          <div className="p-4 border rounded">
            <h3 className="text-sm font-medium">Eligibility</h3>
            <TextInput
              value={form.eligibility.ageGroup}
              onChange={(val: any) => setForm({ ...form, eligibility: { ...form.eligibility, ageGroup: val } })}
              placeholder="e.g., 10+"
            />
            <TextInput
              value={form.eligibility.criteria}
              onChange={(val: any) => setForm({ ...form, eligibility: { ...form.eligibility, criteria: val } })}
              placeholder="e.g., Basic math skills"
            />
          </div>
        </div>
      )}

      {currentStep === 3 && (
        <div className="p-4 border rounded animate-fadeIn">
          <h2 className="text-xl font-bold mb-4">Step 3: Curriculum & Duration</h2>
          <div className="p-4 border rounded mb-4">
            <h3 className="text-sm font-medium">Curriculum PDF</h3>
            <input type="file" accept=".pdf" onChange={handlePdfFileChange} disabled={isPdfUploading} />
            {isPdfUploading && <p className="text-xs text-gray-500 mt-1">Uploading PDF...</p>}
            {pdfUploadError && <p className="text-xs text-red-500 mt-1">{pdfUploadError}</p>}
            {!isPdfUploading && form.curriculumPdfUrl && (
              <p className="text-xs text-gray-500 mt-1">
                Current file:{' '}
                <a href={form.curriculumPdfUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500">
                  {curriculumPdfFile?.name || 'View PDF'}
                </a>
              </p>
            )}
          </div>

          <div className="p-4 border rounded mb-4">
            <h3 className="text-sm font-medium">Curriculum Details</h3>
            {form.curriculum.map((sem, semIdx) => (
              <div key={semIdx} className="p-2 border rounded mt-2">
                <div className="flex items-center gap-2">
                  <TextInput
                    value={sem.title}
                    onChange={(val: any) => handleArrayChange('curriculum', semIdx, 'title', val)}
                    placeholder="Semester Title"
                  />
                  <MdClose
                    className="cursor-pointer text-red-500"
                    onClick={() => removeFromArray('curriculum', semIdx)}
                  />
                </div>
                <div className="ml-4 mt-2">
                  <h4 className="text-xs font-bold">Items</h4>
                  {sem.items.map((item, itemIdx) => (
                    <div key={itemIdx} className="flex items-center gap-2 mt-1">
                      <TextInput
                        value={item.title}
                        onChange={(val: any) => {
                          const newItems = [...sem.items];
                          newItems[itemIdx] = { ...newItems[itemIdx], title: val };
                          handleArrayChange('curriculum', semIdx, 'items', newItems);
                        }}
                        placeholder="Item Title"
                      />
                      <TextInput
                        value={item.description}
                        onChange={(val: any) => {
                          const newItems = [...sem.items];
                          newItems[itemIdx] = { ...newItems[itemIdx], description: val };
                          handleArrayChange('curriculum', semIdx, 'items', newItems);
                        }}
                        placeholder="Item Description"
                      />
                      <MdClose
                        className="cursor-pointer text-red-500"
                        onClick={() => {
                          const newItems = sem.items.filter((_, i) => i !== itemIdx);
                          handleArrayChange('curriculum', semIdx, 'items', newItems);
                        }}
                      />
                    </div>
                  ))}
                  <Button
                    text="Add Item"
                    onClick={() => {
                      const newItems = [...sem.items, { title: '', description: '' }];
                      handleArrayChange('curriculum', semIdx, 'items', newItems);
                    }}
                    size="sm"
                    className="mt-2"
                  />
                </div>
              </div>
            ))}
            <Button
              text="Add Semester"
              onClick={() => addToArray('curriculum', { title: '', items: [] })}
              className="mt-2"
            />
          </div>

          <div className="p-4 border rounded mb-4">
            <h3 className="text-sm font-medium">Duration Options</h3>
            {form.durationOptions.map((opt, i) => (
              <div key={i} className="p-2 border rounded mt-2">
                <div className="flex items-center gap-2">
                  <TextInput
                    value={opt.groupTitle}
                    onChange={(val: any) => handleArrayChange('durationOptions', i, 'groupTitle', val)}
                    placeholder="Group Title"
                  />
                  <TextInput
                    value={opt.sessions}
                    onChange={(val: any) => handleArrayChange('durationOptions', i, 'sessions', val)}
                    placeholder="Sessions"
                  />
                  <MdClose
                    className="cursor-pointer text-red-500"
                    onClick={() => removeFromArray('durationOptions', i)}
                  />
                </div>
                <div className="ml-4 mt-2">
                  <h4 className="text-xs font-bold">Bullets</h4>
                  {opt.bullets.map((bullet, bIdx) => (
                    <div key={bIdx} className="flex items-center gap-2 mt-1">
                      <TextInput
                        value={bullet}
                        onChange={(val: string) => {
                          const newBullets = [...opt.bullets];
                          newBullets[bIdx] = val;
                          handleArrayChange('durationOptions', i, 'bullets', newBullets);
                        }}
                        placeholder="Bullet point"
                      />
                      <MdClose
                        className="cursor-pointer text-red-500"
                        onClick={() => {
                          const newBullets = opt.bullets.filter((_, idx) => idx !== bIdx);
                          handleArrayChange('durationOptions', i, 'bullets', newBullets);
                        }}
                      />
                    </div>
                  ))}
                  <Button
                    text="Add Bullet"
                    onClick={() => handleArrayChange('durationOptions', i, 'bullets', [...opt.bullets, ''])}
                    size="sm"
                    className="mt-2"
                  />
                </div>
              </div>
            ))}
            <Button
              text="Add Duration Option"
              onClick={() => addToArray('durationOptions', { groupTitle: '', sessions: '', bullets: [] })}
              className="mt-2"
            />
          </div>

          <div className="p-4 border rounded">
            <h3 className="text-sm font-medium">Flexibility</h3>
            {form.flexibility.map((f, i) => (
              <div key={i} className="flex items-center gap-2 mt-2">
                <TextInput
                  value={f.title}
                  onChange={(val: any) => handleArrayChange('flexibility', i, 'title', val)}
                  placeholder="Title"
                />
                <TextInput
                  value={f.description}
                  onChange={(val: any) => handleArrayChange('flexibility', i, 'description', val)}
                  placeholder="Description"
                />
                <MdClose className="cursor-pointer text-red-500" onClick={() => removeFromArray('flexibility', i)} />
              </div>
            ))}
            <Button
              text="Add Flexibility"
              onClick={() => addToArray('flexibility', { title: '', description: '' })}
              className="mt-2"
            />
          </div>
        </div>
      )}

      {currentStep === 4 && (
        <div className="p-4 border rounded animate-fadeIn">
          <h2 className="text-xl font-bold mb-4">Step 4: Page Content & SEO</h2>
          <div className="p-4 border rounded mb-4">
            <h3 className="text-sm font-medium">Hero Data</h3>
            <TextInput
              value={typeof form.heroData.title === 'string' ? form.heroData.title : String(form.heroData.title ?? '')}
              onChange={(val: string) => setForm({ ...form, heroData: { ...form.heroData, title: val } })}
              placeholder="Hero Title"
            />
            <label className="text-sm font-medium mt-2">Description</label>
            <textarea
              value={form.heroData.description}
              onChange={(e) => setForm({ ...form, heroData: { ...form.heroData, description: e.target.value } })}
              placeholder="Hero Description"
              className="w-full p-2 border rounded"
              rows={3}
            />

            <div className="mt-2">
              <h4 className="text-xs font-bold">Stats</h4>
              {form.heroData.stats.map((stat, i) => (
                <div key={i} className="flex items-center gap-2 mt-1">
                  <TextInput
                    value={stat}
                    onChange={(val: string) => {
                      const newStats = [...form.heroData.stats];
                      newStats[i] = val;
                      setForm({ ...form, heroData: { ...form.heroData, stats: newStats } });
                    }}
                    placeholder="Stat"
                  />
                  <MdClose
                    className="cursor-pointer text-red-500"
                    onClick={() => {
                      const newStats = form.heroData.stats.filter((_, idx) => idx !== i);
                      setForm({ ...form, heroData: { ...form.heroData, stats: newStats } });
                    }}
                  />
                </div>
              ))}
              <Button
                text="Add Stat"
                onClick={() =>
                  setForm({ ...form, heroData: { ...form.heroData, stats: [...form.heroData.stats, ''] } })
                }
                size="sm"
                className="mt-2"
              />
            </div>

            {/* <div className="mt-2 p-2 border rounded flex">
              <h4 className="text-xs font-bold">Video Card</h4>
              <TextInput value={form.heroData.videoCard.title} onChange={(val) => setForm({ ...form, heroData: { ...form.heroData, videoCard: { ...form.heroData.videoCard, title: val } } })} placeholder="Video Card Title" />
              <TextInputption" value={form.heroData.videoCard.description} onChange={(val) => setForm({ ...form, heroData: { ...form.heroData, videoCard: { ...form.heroData.videoCard, description: val } } })} placeholder="Video Card Description" />
            </div> */}
          </div>

          <div className="p-4 border flex flex-col gap-4 rounded mb-4">
            <h3 className="text-sm font-medium">FAQs</h3>
            {form.faqs.map((faq, i) => (
              <div key={i} className="p-2 border rounded mt-2">
                <div className="flex items-center gap-2">
                  <TextInput
                    value={faq.question}
                    onChange={(val: any) => handleArrayChange('faqs', i, 'question', val)}
                    placeholder="Question"
                  />
                  <MdClose className="cursor-pointer text-red-500" onClick={() => removeFromArray('faqs', i)} />
                </div>
                <textarea
                  value={faq.answer}
                  onChange={(e) => handleArrayChange('faqs', i, 'answer', e.target.value)}
                  placeholder="Answer"
                  className="w-full p-2 border rounded mt-1"
                  rows={2}
                />
              </div>
            ))}
            <Button text="Add FAQ" onClick={() => addToArray('faqs', { question: '', answer: '' })} className="mt-2" />
          </div>

          <div className="p-4 border flex flex-col gap-4 rounded">
            <h3 className="text-sm font-medium">Meta</h3>
            <TextInput
              value={form.meta.title ?? ''}
              onChange={(val: any) => setForm({ ...form, meta: { ...form.meta, title: val } })}
              placeholder="Meta Title"
            />
            <TextInput
                  value={form.meta.description ?? ''}
              onChange={(val: any) => setForm({ ...form, meta: { ...form.meta, description: val } })}
              placeholder="Meta Description"
            />
            <TextInput
              value={form.meta.focusKeyword ?? ''}
              onChange={(val: any) => setForm({ ...form, meta: { ...form.meta, focusKeyword: val } })}
              placeholder="Focus Keyword"
            />
          </div>
        </div>
      )}

      <div className="flex justify-between mt-6">
        {currentStep > 1 && <Button text="Previous" onClick={prevStep} />}
        <div />
        {currentStep < totalSteps && <Button text="Next" onClick={nextStep} />}
        {currentStep === totalSteps && <Button text="Save Subject" onClick={handleSave} />}
      </div>
    </div>
  );
}
